/*
 * Minimal, safe Markdown renderer (no dangerouslySetInnerHTML, no deps) for the
 * legal documents and help articles. Supports headings, bold/italic/code,
 * unordered/ordered lists, links, blockquotes and paragraphs — enough for our
 * content. Anything fancier degrades to plain text.
 */
function inline(text, keyBase) {
  // Split on **bold**, *italic*, `code`, [label](url) — kept simple & safe.
  const parts = []
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g
  let last = 0
  let m
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index))
    if (m[2] != null) parts.push(<strong key={`${keyBase}-${i}`}>{m[2]}</strong>)
    else if (m[3] != null) parts.push(<em key={`${keyBase}-${i}`}>{m[3]}</em>)
    else if (m[4] != null) parts.push(<code key={`${keyBase}-${i}`} style={{ background: 'var(--color-bg-lilac-strong)', padding: '1px 5px', borderRadius: 5, fontSize: '.92em' }}>{m[4]}</code>)
    else if (m[5] != null) parts.push(<a key={`${keyBase}-${i}`} href={m[6]} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)' }}>{m[5]}</a>)
    last = m.index + m[0].length
    i += 1
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

export default function Markdown({ content, style }) {
  const lines = (content || '').replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  let list = null // { ordered, items: [] }
  const flush = () => { if (list) { blocks.push(list); list = null } }

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd()
    const h = /^(#{1,4})\s+(.*)$/.exec(line)
    const ul = /^[-*]\s+(.*)$/.exec(line)
    const ol = /^\d+\.\s+(.*)$/.exec(line)
    const bq = /^>\s?(.*)$/.exec(line)
    if (h) { flush(); blocks.push({ type: 'h', level: h[1].length, text: h[2], key: idx }) }
    else if (ul) { if (!list || list.ordered) { flush(); list = { ordered: false, items: [], key: idx } } list.items.push({ text: ul[1], key: idx }) }
    else if (ol) { if (!list || !list.ordered) { flush(); list = { ordered: true, items: [], key: idx } } list.items.push({ text: ol[1], key: idx }) }
    else if (bq) { flush(); blocks.push({ type: 'bq', text: bq[1], key: idx }) }
    else if (line.trim() === '') { flush() }
    else { flush(); blocks.push({ type: 'p', text: line, key: idx }) }
  })
  flush()

  const hSize = { 1: 26, 2: 21, 3: 17, 4: 15 }
  return (
    <div style={{ color: 'var(--color-text)', fontSize: 14.5, lineHeight: 1.7, ...style }}>
      {blocks.map((b) => {
        if (b.type === 'h') return <div key={b.key} style={{ fontFamily: 'var(--font-title)', fontWeight: b.level <= 2 ? 800 : 700, fontSize: hSize[b.level] || 15, color: 'var(--color-ink)', margin: b.level === 1 ? '4px 0 16px' : '22px 0 8px' }}>{inline(b.text, b.key)}</div>
        if (b.type === 'p') return <p key={b.key} style={{ margin: '0 0 12px' }}>{inline(b.text, b.key)}</p>
        if (b.type === 'bq') return <blockquote key={b.key} style={{ borderLeft: '3px solid var(--color-border-strong)', paddingLeft: 14, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>{inline(b.text, b.key)}</blockquote>
        const Tag = b.ordered ? 'ol' : 'ul'
        return <Tag key={b.key} style={{ margin: '0 0 12px', paddingLeft: 22 }}>{b.items.map((it) => <li key={it.key} style={{ margin: '4px 0' }}>{inline(it.text, it.key)}</li>)}</Tag>
      })}
    </div>
  )
}
